import { Injectable } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { AuthenticationService } from '../authentication.service';
import { GoogleTagManagerService } from './google-tag-manager.service';
import { EventsHubService } from '../../events-hub.service';
import { environment } from '../../../../environments/environment';

/**
 * Keeps the GTM data layer's `userId` in step with the logged-in user.
 *
 * `AuthenticationService` pushes identity at the points where it changes (login, logout, profile
 * refresh), which is the correct ownership. This service is the safety net for *ordering*: it
 * re-asserts the current identity on every `NavigationStart`, which runs before route components
 * are activated and before Angular writes the URL via pushState/replaceState. That means identity
 * is in the data-layer model before both the container's History Change `page_view` tag and any
 * `*_view` event a component pushes from `ngOnInit` — regardless of when the auth service happened
 * to be constructed.
 *
 * `GoogleTagManagerService.setUser` is idempotent, so the repeated assertion costs one data-layer
 * entry per actual identity change, not one per navigation.
 *
 * See the `userId` contract in ./CLAUDE.md.
 */
@Injectable({ providedIn: 'root' })
export class AnalyticsIdentityService {

  constructor(
    private router: Router,
    private authenticationService: AuthenticationService,
    private googleTagManagerService: GoogleTagManagerService,
    private eventsHubService: EventsHubService
  ) {}

  /**
   * Starts watching for identity changes. Invoked from an APP_INITIALIZER so it is running before
   * the router performs its first navigation.
   *
   * Neither subscription uses `takeUntil(destroy$)`, and that is deliberate rather than an oversight:
   * this is a root-provided singleton with no `ngOnDestroy`, so both must stay live for the whole
   * application lifetime. Tearing them down would silently stop identity from being re-asserted. The
   * repo's usual RxJS cleanup rule applies to components, which are destroyed and recreated.
   */
  public init(): void {
    this.syncIdentity();

    this.router.events.pipe(
      filter(event => event instanceof NavigationStart)
    ).subscribe(() => this.syncIdentity());

    this.eventsHubService.isLoggedIn$.subscribe(() => this.syncIdentity());
  }

  /**
   * Pushes the currently stored user's id, or null when nobody is logged in. Warns in dev builds
   * about the one state that silently loses attribution: authenticated but with no user object.
   */
  public syncIdentity(): void {
    const loggedUser = this.authenticationService.getLoggedUser();

    // `== null` rather than a truthiness check: user id 0 is the valid system account, and `!0` would
    // raise this warning for a session that is in fact perfectly attributable.
    if (!environment.production && loggedUser?.id == null && this.authenticationService.isLoggedIn()) {
      console.warn(
        '[analytics] Session is authenticated but no loggedUser is stored, so events cannot be ' +
        'attributed (finding C1). AuthenticationService.init() should be repairing this; if the ' +
        'warning persists, that re-fetch failed.'
      );
    }

    this.googleTagManagerService.setUser(loggedUser?.id);
  }
}
